<template>
  <div class="property-fee-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('propertyFee.title')" :subtitle="$t('propertyFee.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="goBack">
          {{ $t('common.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- 主费用信息组件 -->
    <view-main-fee ref="viewMainFee" :fee-name="$t('propertyFee.feeName')" fee-type-cd="888800010001"
      pay-name="propertyPay" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-date-picker v-model="feeDetailInfo.startTime" type="date" class="w-full"
            :placeholder="$t('propertyFee.selectStartTime')" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-date-picker v-model="feeDetailInfo.endTime" type="date" class="w-full"
            :placeholder="$t('propertyFee.selectEndTime')" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="queryFeeDetailMethod()">
          {{ $t('propertyFee.searchNow') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetFeeDetailMethod()">
          {{ $t('propertyFee.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="queryFeeDetailMethod">
        <template #left>
          <span class="ds-panel__title">{{ $t('propertyFee.paymentHistory') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="feeDetailInfo.feeDetails" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('propertyFee.page.empty')" @retry="queryFeeDetailMethod">
        <el-table-column prop="detailId" :label="$t('propertyFee.paymentId')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.detailId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="cycles" :label="$t('propertyFee.cycle')" align="center" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.cycles || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="primeRateName" :label="$t('propertyFee.paymentMethod')" align="center" width="130">
          <template slot-scope="scope">
            {{ scope.row.primeRateName || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('propertyFee.receivableAmount')" align="right" width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.state == '1500'" class="ds-text-muted">
              {{ scope.row.payerObjName }}{{ $t('propertyFee.viewInFee') }}
            </span>
            <span v-else class="ds-num">{{ formatMoney(scope.row.receivableAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('propertyFee.receivedAmount')" align="right" width="180">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.receivedAmount) }}</span>
            <div v-if="scope.row.acctAmount > 0" class="ds-amount-sub">
              {{ $t('propertyFee.accountDeduction') }}: {{ formatMoney(scope.row.acctAmount) }}
            </div>
            <div v-for="(item, index) in scope.row.payFeeDetailDiscountDtoList" :key="index" class="ds-amount-sub">
              {{ item.discountName }}: {{ formatMoney(Math.abs(item.discountPrice)) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('propertyFee.paymentTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('propertyFee.startTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ dateFormat(scope.row.startTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('propertyFee.endTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ dateFormat(scope.row.endTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="cashierName" :label="$t('propertyFee.cashier')" align="center" width="120">
          <template slot-scope="scope">
            {{ scope.row.cashierName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('propertyFee.status')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag type="info" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('propertyFee.remark')" align="left" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="ds-text-muted">{{ scope.row.remark || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('propertyFee.operation')" align="center" fixed="right" width="150">
          <template slot-scope="scope">
            <el-button v-if="scope.row.state == '1400' || scope.row.state == '1200' || scope.row.state == ''" size="mini"
              type="text" @click="_openRefundModel(scope.row)">
              {{ $t('propertyFee.applyRefund') }}
            </el-button>
            <el-button v-if="scope.row.state != '1500'" size="mini" type="text" @click="_openFeeDetailDiscountModal(scope.row)">
              {{ $t('propertyFee.discount') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('propertyFee.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 子组件 -->
    <return-pay-fee ref="returnPayFee" @success="queryFeeDetailMethod" />
    <view-fee-detail-discount ref="viewFeeDetailDiscount" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { queryFeeDetail } from '@/api/fee/propertyFeeApi'
import ViewMainFee from '@/components/fee/viewMainFee'
import ReturnPayFee from '@/components/fee/returnPayFee'
import ViewFeeDetailDiscount from '@/components/fee/viewFeeDetailDiscount'
import { dateFormat } from '@/utils/dateUtil'
import { formatDateTimeSec, formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'PropertyFeeList',
  components: {
    ViewMainFee,
    ReturnPayFee,
    ViewFeeDetailDiscount,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      feeDetailInfo: {
        feeDetails: [],
        total: 0,
        records: 1,
        feeId: '',
        startTime: '',
        endTime: ''
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.feeDetailInfo.feeId = this.$route.query.feeId
    this.queryFeeDetailMethod()
  },
  methods: {
    dateFormat(date) {
      if (!date) return ''
      return dateFormat(date)
    },
    async listFeeDetail(page = 1, row = 10) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page,
          row,
          communityId: this.communityId,
          feeId: this.feeDetailInfo.feeId,
          startTime: this.feeDetailInfo.startTime,
          endTime: this.feeDetailInfo.endTime
        }

        const { feeDetails, total, records } = await queryFeeDetail(params)
        this.feeDetailInfo.feeDetails = Array.isArray(feeDetails) ? feeDetails : []
        this.feeDetailInfo.total = total
        this.feeDetailInfo.records = records
        this.page.total = records
      } catch (error) {
        console.error('获取费用明细失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('propertyFee.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    queryFeeDetailMethod() {
      this.page.current = 1
      this.listFeeDetail()
    },
    resetFeeDetailMethod() {
      this.feeDetailInfo.startTime = ''
      this.feeDetailInfo.endTime = ''
      this.queryFeeDetailMethod()
    },
    _openRefundModel(feeDetail) {
      this.$refs.returnPayFee.open(feeDetail)
    },
    _openFeeDetailDiscountModal(detail) {
      this.$refs.viewFeeDetailDiscount.open(detail)
    },
    handleSizeChange(val) {
      this.page.size = val
      this.listFeeDetail(this.page.current, val)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.listFeeDetail(val, this.page.size)
    },
    goBack() {
      this.$router.go(-1)
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.property-fee-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 辅助文本 */
.ds-text-muted {
  color: var(--ds-gray-500);
}

/* 金额明细副行 */
.ds-amount-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}
</style>
